import { useState } from "react";
import { SmilePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { getAutoreacts, createAutoreact, deleteAutoreact, toggleAutoreact } from "@/api/autoreacts";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { Modal } from "./Modal";
import { showApiError } from "./helpers";
import { Field, TextInput, ChoiceSelect, Toggle } from "./fields";

const MATCH_TYPES = ["contains", "exact", "startswith", "endswith"];

export const AutoReacts = ({ guildId }) => {
  const { data, loading, error, refetch } = useApi(() => getAutoreacts(guildId));
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const items = data?.autoreacts || [];

  const submit = async () => {
    const { form } = modal;
    if (!form.trigger.trim() || !form.emoji.trim()) {
      toast.error("Trigger and emoji are both required.");
      return;
    }
    setSaving(true);
    try {
      await createAutoreact(guildId, { ...form, trigger: form.trigger.trim(), emoji: form.emoji.trim() });
      toast.success("Auto react created.");
      setModal(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      await deleteAutoreact(guildId, item.id);
      toast.success("Auto react deleted.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const toggle = async (item) => {
    try {
      await toggleAutoreact(guildId, item.id);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  return (
    <>
      <CrudCard
        icon={SmilePlus}
        title="Auto Reacts"
        description="React with an emoji when a message matches a trigger."
        testId="crud-autoreacts"
        id="autoreacts"
        loading={loading}
        error={error}
        onRetry={refetch}
        isEmpty={items.length === 0}
        emptyText="No auto reacts yet."
        addLabel="New auto react"
        onAdd={() => setModal({ form: { trigger: "", emoji: "", match_type: "contains" } })}
      >
        <ul className="space-y-3">
          {items.map((item) => (
            <ListRow
              key={item.id}
              testId={`autoreact-row-${item.id}`}
              actions={
                <>
                  <Toggle checked={!!item.enabled} onChange={() => toggle(item)} label={`Toggle ${item.trigger}`} testId={`autoreact-toggle-${item.id}`} />
                  <IconButton onClick={() => remove(item)} label="Delete" danger testId={`autoreact-delete-${item.id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                </>
              }
            >
              <p className="truncate font-mono text-sm text-paper-100">
                {item.emoji} <span className="text-paper-500">on</span> {item.trigger}
              </p>
              <p className="mt-0.5 font-mono text-xs text-paper-500">{item.match_type}</p>
            </ListRow>
          ))}
        </ul>
      </CrudCard>

      <Modal open={!!modal} onClose={() => setModal(null)} title="New auto react" testId="autoreact-modal">
        {modal && (
          <div className="space-y-4">
            <Field label="Trigger">
              <TextInput value={modal.form.trigger} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, trigger: v } }))} placeholder="gg" testId="autoreact-form-trigger" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Emoji" hint="Unicode emoji or custom emoji format.">
                <TextInput value={modal.form.emoji} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, emoji: v } }))} placeholder="🔥" testId="autoreact-form-emoji" />
              </Field>
              <Field label="Match type">
                <ChoiceSelect value={modal.form.match_type} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, match_type: v } }))} options={MATCH_TYPES} testId="autoreact-form-match" />
              </Field>
            </div>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="autoreact-form-save"
              className="w-full rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create auto react"}
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};
